import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

export class InvalidPremium {
  public readonly key = 'InvalidPremium';

  constructor(
    public readonly weekly: StringUnion,
    public readonly biWeekly: StringUnion,
    public readonly twentyEightDays: StringUnion,
    public readonly semiMonthly: StringUnion,
    public readonly monthly: StringUnion,
    public readonly quarterly: StringUnion,
    public readonly semiAnnual: StringUnion,
    public readonly ninthly: StringUnion,
    public readonly tenthly: StringUnion,
    public readonly thirteenthly: StringUnion,
    public readonly annually: StringUnion,
    public readonly cwa: StringUnion,
    public readonly billMode: StringUnion,
    public readonly productName: StringUnion,
    public readonly section125: boolean,
    public readonly deathBenefit: number,
    public readonly replacementPremium: number,
    public readonly eeiIndicator: string,
    public readonly replacement: boolean,
    public readonly overrideAnnualPremium: boolean
  ) {}
}

type AppProps = {
  weekly: StringUnion;
  biWeekly: StringUnion;
  twentyEightDays: StringUnion;
  semiMonthly: StringUnion;
  monthly: StringUnion;
  quarterly: StringUnion;
  semiAnnual: StringUnion;
  ninthly: StringUnion;
  tenthly: StringUnion;
  thirteenthly: StringUnion;
  annually: StringUnion;
  cwa: StringUnion;
  billMode: StringUnion;
  productName?: StringUnion;
  section125: boolean;
  deathBenefit: number;
  replacementPremium: number;
  eeiIndicator: string;
  replacement: boolean;
  overrideAnnualPremium: boolean;
};

export class ValidPremium {
  public static createFromApp({
    weekly,
    biWeekly,
    twentyEightDays,
    semiMonthly,
    monthly,
    quarterly,
    semiAnnual,
    ninthly,
    tenthly,
    thirteenthly,
    annually,
    cwa,
    billMode,
    productName,
    section125,
    deathBenefit,
    replacementPremium,
    eeiIndicator,
    replacement,
    overrideAnnualPremium,
  }: AppProps) {
    return this.create(
      weekly,
      biWeekly,
      twentyEightDays,
      semiMonthly,
      monthly,
      quarterly,
      semiAnnual,
      ninthly,
      tenthly,
      thirteenthly,
      annually,
      cwa,
      billMode,
      productName!,
      section125,
      deathBenefit,
      replacementPremium,
      eeiIndicator,
      replacement,
      overrideAnnualPremium
    );
  }

  public static create(
    weekly: StringUnion,
    biWeekly: StringUnion,
    twentyEightDays: StringUnion,
    semiMonthly: StringUnion,
    monthly: StringUnion,
    quarterly: StringUnion,
    semiAnnual: StringUnion,
    ninthly: StringUnion,
    tenthly: StringUnion,
    thirteenthly: StringUnion,
    annually: StringUnion,
    cwa: StringUnion,
    billMode: StringUnion,
    productName: StringUnion,
    section125: boolean,
    deathBenefit: number,
    replacementPremium: number,
    eeiIndicator: string,
    replacement: boolean,
    overrideAnnualPremium: boolean
  ) {
    return isNonEmptyString(weekly) || isNonEmptyString(monthly)
      ? new ValidPremium(
          weekly,
          biWeekly,
          twentyEightDays,
          semiMonthly,
          monthly,
          quarterly,
          semiAnnual,
          ninthly,
          tenthly,
          thirteenthly,
          annually,
          cwa,
          billMode,
          productName,
          section125,
          deathBenefit,
          replacementPremium,
          eeiIndicator,
          replacement,
          overrideAnnualPremium
        )
      : new InvalidPremium(
          weekly,
          biWeekly,
          twentyEightDays,
          semiMonthly,
          monthly,
          quarterly,
          semiAnnual,
          ninthly,
          tenthly,
          thirteenthly,
          annually,
          cwa,
          billMode,
          productName,
          section125,
          deathBenefit,
          replacementPremium,
          eeiIndicator,
          replacement,
          overrideAnnualPremium
        );
  }

  public readonly key = 'ValidPremium';
  private constructor(
    public readonly weekly: StringUnion,
    public readonly biWeekly: StringUnion,
    public readonly twentyEightDays: StringUnion,
    public readonly semiMonthly: StringUnion,
    public readonly monthly: StringUnion,
    public readonly quarterly: StringUnion,
    public readonly semiAnnual: StringUnion,
    public readonly ninthly: StringUnion,
    public readonly tenthly: StringUnion,
    public readonly thirteenthly: StringUnion,
    public readonly annually: StringUnion,
    public readonly cwa: StringUnion,
    public readonly billMode: StringUnion,
    public readonly productName: StringUnion,
    public readonly section125: boolean,
    public readonly deathBenefit: number,
    public readonly replacementPremium: number,
    public readonly eeiIndicator: string,
    public readonly replacement: boolean,
    public readonly overrideAnnualPremium: boolean
  ) {}
}

export type Premium = InvalidPremium | ValidPremium;

export type PremiumOptions<TReturn> = ToPartialMatchOptions<Premium, 'key', TReturn>;

export const matchPremium = <TReturn>(options: PremiumOptions<TReturn>) => (app: Premium): TReturn => {
  switch (app.key) {
    case 'InvalidPremium':
      return options.InvalidPremium ? options.InvalidPremium(app) : (options as any).default(app);
    case 'ValidPremium':
      return options.ValidPremium ? options.ValidPremium(app) : (options as any).default(app);
  }
};

export const changeWeekly = (weeklyPremium: string, productName: string) => (app: Premium) => {
  const annual = Number(weeklyPremium) * 52;
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    weekly: NonEmptyString.create(weeklyPremium),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
    productName: NonEmptyString.create(productName),
  });
};

export const changeBiWeekly = (biWeeklyPremium: string) => (app: Premium) => {
  const annual = Number(biWeeklyPremium) * 26;
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    biWeekly: NonEmptyString.create(biWeeklyPremium),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const change28Days = (twentyEightDaysPremium: string) => (app: Premium) => {
  const annual = Number(twentyEightDaysPremium) * 13;
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biWeeklyPrem = parseFloat((annual / 26).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    twentyEightDays: NonEmptyString.create(twentyEightDaysPremium),
    biWeekly: NonEmptyString.create(biWeeklyPrem.toString()),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const changeSemiMonthly = (semiMonthlyPremium: string) => (app: Premium) => {
  const annual = Number(semiMonthlyPremium) * 24;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    semiMonthly: NonEmptyString.create(semiMonthlyPremium),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const changeMonthly = (monthlyPremium: string, productName: string) => (app: Premium) => {
  const annual = Number(monthlyPremium) * 12;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    monthly: NonEmptyString.create(monthlyPremium),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
    productName: NonEmptyString.create(productName),
  });
};

export const changeQuarterly = (quarterlyPremium: string) => (app: Premium) => {
  const annual = Number(quarterlyPremium) * 4;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    quarterly: NonEmptyString.create(quarterlyPremium),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};
export const changeSemiAnnual = (semiAnnualPremium: string) => (app: Premium) => {
  const annual = Number(semiAnnualPremium) * 2;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    semiAnnual: NonEmptyString.create(semiAnnualPremium),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const changeNinthly = (ninthlyPremium: string) => (app: Premium) => {
  const annual = Number(ninthlyPremium) * 9;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    ninthly: NonEmptyString.create(ninthlyPremium),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const changeTenthly = (tenthlyPremium: string) => (app: Premium) => {
  const annual = Number(tenthlyPremium) * 9;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const thirteenthlyPrem = parseFloat((annual / 13).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    tenthly: NonEmptyString.create(tenthlyPremium),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const changeThirteenly = (thirteenlyPremium: string) => (app: Premium) => {
  const annual = Number(thirteenlyPremium) * 13;
  const weeklyPrem = parseFloat((annual / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annual / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annual / 13).toFixed(2));
  const monthlyPrem = parseFloat((annual / 12).toFixed(2));
  const quarterlyPrem = parseFloat((annual / 4).toFixed(2));
  const semiMonthlyPrem = parseFloat((annual / 24).toFixed(2));
  const semiAnnualPrem = parseFloat((annual / 2).toFixed(2));
  const ninthlyPrem = parseFloat((annual / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annual / 10).toFixed(2));
  const annualPrem = parseFloat(annual.toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    thirteenthly: NonEmptyString.create(thirteenlyPremium),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
    annually: NonEmptyString.create(annualPrem.toString()),
  });
};

export const changeAnnually = (AnnualPremium: string) => (app: Premium) => {
  const annualPremiumValue = Number(AnnualPremium);

  const weeklyPrem = parseFloat((annualPremiumValue / 52).toFixed(2));
  const biweeklyPrem = parseFloat((annualPremiumValue / 26).toFixed(2));
  const twentyEightDayPrem = parseFloat((annualPremiumValue / 13).toFixed(2));
  const monthlyPrem = parseFloat((annualPremiumValue / 12).toFixed(2));
  const quarterlyPrem = parseFloat((annualPremiumValue / 4).toFixed(2));
  const semiMonthlyPrem = parseFloat((annualPremiumValue / 24).toFixed(2));
  const semiAnnualPrem = parseFloat((annualPremiumValue / 2).toFixed(2));
  const ninthlyPrem = parseFloat((annualPremiumValue / 9).toFixed(2));
  const tenthlyPrem = parseFloat((annualPremiumValue / 10).toFixed(2));
  const thirteenthlyPrem = parseFloat((annualPremiumValue / 13).toFixed(2));

  return ValidPremium.createFromApp({
    ...app,
    annually: NonEmptyString.create(AnnualPremium),
    thirteenthly: NonEmptyString.create(thirteenthlyPrem.toString()),
    tenthly: NonEmptyString.create(tenthlyPrem.toString()),
    ninthly: NonEmptyString.create(ninthlyPrem.toString()),
    semiAnnual: NonEmptyString.create(semiAnnualPrem.toString()),
    quarterly: NonEmptyString.create(quarterlyPrem.toString()),
    weekly: NonEmptyString.create(weeklyPrem.toString()),
    biWeekly: NonEmptyString.create(biweeklyPrem.toString()),
    monthly: NonEmptyString.create(monthlyPrem.toString()),
    twentyEightDays: NonEmptyString.create(twentyEightDayPrem.toString()),
    semiMonthly: NonEmptyString.create(semiMonthlyPrem.toString()),
  });
};

export const changeCWA = (cwaValue: string) => (app: Premium) =>
  ValidPremium.createFromApp({
    ...app,
    cwa: NonEmptyString.create(cwaValue),
  });

export const changeBillMode = (billMode: string) => (app: Premium) =>
  ValidPremium.createFromApp({
    ...app,
    billMode: NonEmptyString.create(billMode),
  });

export const changeOverrideAnnualPremium = (overrideAnnualPremium: boolean) => (app: Premium) =>
  ValidPremium.createFromApp({
    ...app,
    overrideAnnualPremium,
  });

export const changeSection125 = (section125: boolean) => (app: Premium) =>
  ValidPremium.createFromApp({
    ...app,
    section125,
  });

export const EmptyPremium = new InvalidPremium(
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  0,
  0,
  '',
  false,
  false
);
